import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import useAuthStore from '../../store/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_0a775788-3b72-4cac-99b0-2ae8be67d728/artifacts/5ne6bwob_Alhaj_Halfan_Swai_Logo.png';

const MemberLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuthStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      if (response.status === 200) {
        if (response.data.user.role !== 'member') {
          toast.error('Please use the admin portal to login.');
          return;
        }
        
        setUser(response.data.user, response.data.token);
        toast.success('Login successful!');
        navigate('/member');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
      </div>
      
      <div className="relative z-10 w-full max-w-md">
        <Card className="bg-white/95 backdrop-blur-md shadow-2xl border border-slate-200">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img src={LOGO_URL} alt="AHSGA Logo" className="w-24 h-24 object-contain" />
            </div>
            <h1 className="text-4xl font-extrabold text-slate-900 tracking-wide" style={{ fontFamily: "'Poppins', sans-serif" }}>AHSGA</h1>
            <CardTitle className="text-2xl font-bold text-slate-700 mt-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Member Portal
            </CardTitle>
            <p className="text-slate-500 mt-2 text-sm">Sign in to access your account</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="you@ahsga.me"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="submit-button"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <Link
                to="/forgot-password"
                className="text-sm text-slate-600 hover:text-slate-900 block"
              >
                Forgot your password?
              </Link>
              <Link
                to="/register"
                className="text-sm text-slate-600 hover:text-slate-900 block"
              >
                Don't have an account? Register
              </Link>
              <Link
                to="/admin/login"
                className="text-sm text-slate-600 hover:text-slate-900 block"
              >
                Admin? Login here
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-white mt-6 opacity-80">
          © 2026 AHSGA. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default MemberLogin;